const MOB_PIPE_EMPTY_STATES = {
  applied: {
    icon: 'inbox',
    title: 'Nothing in Applied yet',
    body: "Jobs you've saved or applied to will start here. Add a job to begin tracking it through your search.",
    showAdd: true,
  },
  interview: {
    icon: 'forum',
    title: 'No interviews yet',
    body: 'When a job moves forward, switch it to Interview so you can keep active conversations easy to find.',
  },
  ghosted: {
    icon: 'volume_off',
    title: 'No ghosted jobs',
    body: 'Jobs that have gone quiet can live here so they stop cluttering your active pipeline.',
  },
  rejected: {
    icon: 'block',
    title: 'No rejected jobs',
    body: "Rejected or closed roles can go here when you're done tracking them.",
  },
};

function Pipeline({ jobs, onOpen, upsert, onAdd, sortOption, bulkMode, selectedIds, onToggleSelect }) {
  const { key: sortKey, dir: sortDir } = sortFromOption(sortOption);

  const sortValue = (job) => {
    if (sortKey === 'company') return (job.company || '').toLowerCase();
    if (sortKey === 'title') return (job.title || '').toLowerCase();
    if (sortKey === 'date') return job.date || '';
    if (sortKey === 'status') return statusSortNumber(job.status);
    if (sortKey === 'salary') return salarySortNumber(job.salary);
    return '';
  };

  const sortJobs = (items) => {
    return [...items].sort((a, b) => {
      if (sortKey === 'date') return compareJobsByAppliedDate(a, b, sortDir);
      const av = sortValue(a), bv = sortValue(b);
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  };

  const compactSalary = (value) => {
    const range = parseSalaryRange(value);
    if (!range) return null;
    const k = n => `$${Math.round(n / 1000)}k`;
    if (range.openEnded) return `${k(range.low)}+`;
    if (range.low === range.high) return k(range.low);
    return `${k(range.low)}–${k(range.high)}`;
  };

  const today = todayISO();

  // ── Desktop: 4 kanban columns (excludes 'yes') ──────────────────────────────
  const [isDragging, setIsDragging] = React.useState(false);
  const [hoveredCol, setHoveredCol] = React.useState(null);

  const PIPELINE_COLS = STATUSES.filter(s => s.id !== 'yes');
  const grouped = {};
  for (const s of PIPELINE_COLS) grouped[s.id] = [];
  for (const j of jobs) if (grouped[j.status] !== undefined) grouped[j.status].push(j);
  for (const s of PIPELINE_COLS) grouped[s.id] = sortJobs(grouped[s.id]);

  const handleDragStart = (event, jobId) => {
    event.dataTransfer.setData('text/plain', jobId);
    event.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };
  const handleDragEnd = () => { setIsDragging(false); setHoveredCol(null); };
  const handleDrop = (event, status) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    setHoveredCol(null);
    const jobId = event.dataTransfer.getData('text/plain');
    const job = jobs.find(item => item.id === jobId);
    if (!job || job.status === status || !upsert) return;
    upsert({ ...job, status });
  };
  const allowDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
  };

  // ── Mobile: 4 stage selector (excludes 'yes'/Offer) ────────────────────────
  const MOB_PIPE_STAGES = STATUSES.filter(s => s.id !== 'yes');

  const [mobPipeStage, setMobPipeStage] = React.useState(MOB_PIPE_STAGES[0].id);
  const [mobStatusMenuId, setMobStatusMenuId] = React.useState(null);

  const mobGrouped = {};
  for (const s of STATUSES) mobGrouped[s.id] = [];
  for (const j of jobs) if (mobGrouped[j.status] !== undefined) mobGrouped[j.status].push(j);
  for (const s of STATUSES) mobGrouped[s.id] = sortJobs(mobGrouped[s.id]);

  const mobActiveStatus = STATUSES.find(s => s.id === mobPipeStage) || STATUSES[0];
  const mobActiveJobs = mobGrouped[mobPipeStage] || [];

  return (
    <>
      {/* ── Desktop kanban (hidden on mobile via CSS) ── */}
      <div className={`pipeline${isDragging ? ' is-dragging' : ''}`}>
        {jobs.length === 0 ? (
          <div className="pipeline-onboard sketch-box">
            <h3 className="pipeline-onboard-title">Build your first pipeline</h3>
            <p className="pipeline-onboard-body">
              Add a job to start tracking it through Applied, Interview, Ghosted, and Rejected.
              Once jobs are here, you can drag them between stages to keep your search organized.
            </p>
            {onAdd && (
              <button className="candy-btn add" type="button" onClick={onAdd}>+ Add job</button>
            )}
          </div>
        ) : PIPELINE_COLS.map(s => (
          <div
            key={s.id}
            className={`col sketch-box drop-col${hoveredCol === s.id ? ' drop-zone-hover' : ''}`}
            onDragOver={allowDrop}
            onDragEnter={() => setHoveredCol(s.id)}
            onDrop={(event) => handleDrop(event, s.id)}
          >
            <div className="col-head">
              <h3>{s.label}</h3>
              <span className={`pill ${s.cls}`}>{grouped[s.id].length}</span>
            </div>
            <div className="col-list drop-list" onDragOver={allowDrop} onDrop={(event) => handleDrop(event, s.id)}>
              {grouped[s.id].length === 0 && (
                <div className="empty">No jobs here yet. Drag a job into this column to update its status.</div>
              )}
              {grouped[s.id].map(j => {
                const old = daysBetween(j.date, today);
                const stale = s.id === 'applied' && old >= STALE_DAYS;
                const isSelected = bulkMode && selectedIds.has(j.id);
                return (
                  <div
                    key={j.id}
                    className={`job-card draggable-card${isSelected ? ' bulk-selected' : ''}`}
                    draggable={!bulkMode}
                    onDragStart={!bulkMode ? (event) => handleDragStart(event, j.id) : undefined}
                    onDragEnd={!bulkMode ? handleDragEnd : undefined}
                    onClick={() => bulkMode ? onToggleSelect(j.id) : onOpen(j)}
                  >
                    <div className="co" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {!bulkMode && (
                        <span className="material-icons" aria-hidden="true"
                          style={{ fontSize: 13, color: 'var(--ink-soft)', flexShrink: 0, opacity: 0.5 }}>
                          drag_indicator
                        </span>
                      )}
                      {bulkMode && (
                        <input type="checkbox" className="bulk-cb" checked={isSelected}
                          onChange={() => onToggleSelect(j.id)} onClick={e => e.stopPropagation()}
                          style={{ flexShrink: 0 }} />
                      )}
                      {stale && <span className="stale-dot" title={`${old}d old`}></span>}
                      {j.favorite && <span className="material-icons favorite-heart">auto_awesome</span>}
                      {j.company}
                    </div>
                    <div className="ti" style={{ marginTop: 2 }}>{j.title}</div>
                    <div className="meta" style={{ marginTop: 4, flexWrap: 'nowrap' }}>
                      <span>{prettyDate(j.date)}</span>
                      {compactSalary(j.salary) && <span>· {compactSalary(j.salary)}</span>}
                    </div>
                    {((s.id === 'interview' && !bulkMode) || safeHref(j.link)) && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center' }}>
                        {s.id === 'interview' && !bulkMode && (
                          <label
                            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', color: 'var(--ink-soft)' }}
                            onClick={e => e.stopPropagation()}
                          >
                            <input type="checkbox" style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                              onChange={e => { if (e.target.checked) upsert({ ...j, status: 'yes' }); }} />
                            <span className="material-icons" style={{ fontSize: 14, color: 'var(--accent)' }}>handshake</span>
                            <span>Received Offer</span>
                          </label>
                        )}
                        {safeHref(j.link) && (
                          <a href={safeHref(j.link)} target="_blank" rel="noreferrer"
                            onClick={e => e.stopPropagation()} className="candy-btn link-pink"
                            style={{ marginLeft: 'auto', flexShrink: 0, width: 20, height: 20, minHeight: 20, padding: 0, borderRadius: 5 }}>
                            <span className="material-icons" aria-hidden="true" style={{ fontSize: 10, lineHeight: 1 }}>north_east</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── Mobile workflow view (hidden on desktop via CSS) ── */}
      <div className="mob-pipeline">
        {/* Scrim — closes status menu when tapping outside */}
        {mobStatusMenuId && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }}
            onClick={() => setMobStatusMenuId(null)} />
        )}

        {/* 4-item stage grid selector */}
        <div className="mob-pipe-stages">
          {MOB_PIPE_STAGES.map(s => (
            <button
              key={s.id}
              type="button"
              className={`mob-pipe-stage-btn${mobPipeStage === s.id ? ' active' : ''}`}
              onClick={() => { setMobPipeStage(s.id); setMobStatusMenuId(null); }}
            >
              <span className="mob-pipe-count">{mobGrouped[s.id].length}</span>
              <span className="mob-pipe-label">{s.label}</span>
            </button>
          ))}
        </div>

        {/* Job cards */}
        {mobActiveJobs.length === 0 && (() => {
          const es = MOB_PIPE_EMPTY_STATES[mobPipeStage] || {
            icon: 'inbox',
            title: `Nothing in ${mobActiveStatus.label} yet`,
            body: 'When a job belongs in this stage, it will show up here. Use the status switcher on a job card to move it through your pipeline.',
          };
          return (
            <div className="mob-pipe-empty">
              <span className="material-icons mob-pipe-empty-icon">{es.icon}</span>
              <div className="mob-pipe-empty-title">{es.title}</div>
              <div className="mob-pipe-empty-body">{es.body}</div>
              {es.showAdd && onAdd && (
                <button className="candy-btn link-pink mob-pipe-empty-add" type="button" onClick={onAdd}>
                  Add job
                </button>
              )}
            </div>
          );
        })()}
        {mobActiveJobs.map(j => {
          const old = daysBetween(j.date, today);
          const stale = j.status === 'applied' && old >= STALE_DAYS;
          const salStr = parseSalaryRange(j.salary) ? formatSalaryValue(j.salary) : null;
          const isSelected = bulkMode && selectedIds.has(j.id);
          return (
            <div key={j.id} className={`mob-pipe-card${isSelected ? ' bulk-selected' : ''}`}>
              {/* Card body — opens job detail */}
              <div
                className="mob-pipe-card-body"
                onClick={() => bulkMode ? onToggleSelect(j.id) : onOpen(j)}
              >
                <div className="co">
                  {bulkMode && (
                    <input type="checkbox" className="bulk-cb" checked={isSelected}
                      onChange={() => onToggleSelect(j.id)} onClick={e => e.stopPropagation()}
                      style={{ flexShrink: 0, marginRight: 4 }} />
                  )}
                  {stale && <span className="stale-dot" title={`${old}d old`}></span>}
                  {j.favorite && (
                    <span className="material-icons favorite-heart" style={{ fontSize: 13 }}>auto_awesome</span>
                  )}
                  {j.company}
                </div>
                <div className="ti">{j.title}</div>
                {(j.date || salStr) && (
                  <div className="meta">
                    {prettyDate(j.date)}{salStr ? ` · ${salStr}` : ''}
                  </div>
                )}
              </div>

              {/* Action buttons — only in normal mode */}
              {!bulkMode && (
                <div className="mob-pipe-actions">
                  {/* Status change button + popover */}
                  <div className="mob-pipe-status-wrap">
                    <button
                      type="button"
                      className="mob-pipe-action-btn"
                      onClick={e => {
                        e.stopPropagation();
                        setMobStatusMenuId(mobStatusMenuId === j.id ? null : j.id);
                      }}
                      aria-label="Change status"
                    >
                      <span className="material-icons" style={{ fontSize: 18 }}>swap_horiz</span>
                    </button>
                    {mobStatusMenuId === j.id && (
                      <div className="mob-pipe-status-menu">
                        {STATUSES.map(s => (
                          <button
                            key={s.id}
                            type="button"
                            className={`mob-pipe-status-opt${s.id === j.status ? ' current' : ''}`}
                            onClick={e => {
                              e.stopPropagation();
                              if (s.id !== j.status && upsert) upsert({ ...j, status: s.id });
                              setMobStatusMenuId(null);
                            }}
                          >
                            <span className={`pill ${s.cls}`}>{s.label}</span>
                            {s.id === j.status && (
                              <span className="mob-pipe-status-selected">Selected</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* External listing link button — only shown when job has a link */}
                  {safeHref(j.link) && (
                    <button
                      type="button"
                      className="mob-pipe-action-btn open-btn"
                      onClick={e => { e.stopPropagation(); window.open(safeHref(j.link), '_blank', 'noopener,noreferrer'); }}
                      aria-label="View listing"
                    >
                      <span className="material-icons" style={{ fontSize: 16 }}>north_east</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
